/**
 * What the delivery surfaces (#14619) exchange with the server: the shell
 * payload a recipient's banner and popup render from, and the closed lists
 * the record endpoint accepts.
 *
 * CLIENT-SAFE, like `./vocabulary`: no `server-only`, no Prisma — the
 * `AnnouncementHost` in the root layout imports it, and so does the server
 * action that builds the payload.
 */

import { z } from 'zod'

import { validateAnnouncementCtaUrl } from './cta-url'
import type { AnnouncementDisplayMode } from './vocabulary'

/**
 * Where an activity happened, for every delivery event's `surface`.
 * `whats_new` is the `/whats-new` page (the `?announcement=<id>` deep link
 * for `seen`, a card's CTA for `cta_clicked`).
 */
export const ANNOUNCEMENT_SURFACES = ['banner', 'modal', 'whats_new'] as const
export type AnnouncementSurface = (typeof ANNOUNCEMENT_SURFACES)[number]
export const announcementSurfaceSchema = z.enum(ANNOUNCEMENT_SURFACES)

/**
 * What a recipient can do with an announcement. The first three set a
 * timestamp on their recipient row; `later` (the popup's "Maybe later") and
 * `expanded` (the banner's "Show more") are events only (#14953).
 */
export const ANNOUNCEMENT_ACTIVITIES = [
  'seen',
  'dismissed',
  'cta_clicked',
  'later',
  'expanded',
] as const
export type AnnouncementActivity = (typeof ANNOUNCEMENT_ACTIVITIES)[number]
export const announcementActivitySchema = z.enum(ANNOUNCEMENT_ACTIVITIES)

/** An announcement id is a cuid; anything longer is refused unread. */
export const ANNOUNCEMENT_ID_MAX = 50

/**
 * One announcement as the shell renders it. JSON-safe: no `Date`s, and the
 * image arrives as a signed view URL (`getViewUrl`, recipient-authorized)
 * rather than an attachment id the client would have to resolve.
 */
export type DeliveredAnnouncement = {
  id: string
  title: string
  body: string
  iconName: string | null
  imageUrl: string | null
  ctaLabel: string | null
  ctaUrl: string | null
  displayMode: AnnouncementDisplayMode
  celebrate: boolean
}

export type ShellAnnouncementsPayload = {
  announcements: DeliveredAnnouncement[]
}

export const EMPTY_SHELL_ANNOUNCEMENTS: ShellAnnouncementsPayload = {
  announcements: [],
}

/**
 * How a CTA link opens: an in-app path in the same tab, an `https:` URL in a
 * new one (with `rel="noopener noreferrer"`). `null` for anything else.
 *
 * The verdict is `validateAnnouncementCtaUrl`'s — the SAME rule the
 * authoring write enforces — so a row that reached the table some other way
 * (a seed, a backfill, a prod-write) still cannot draw `/\evil.example`,
 * which a browser resolves off-site. A link that fails it is not drawn.
 */
export function announcementCtaTarget(
  url: string | null,
): 'same-tab' | 'new-tab' | null {
  if (!url || url !== url.trim()) return null
  if (!validateAnnouncementCtaUrl(url).ok) return null
  return url.startsWith('/') ? 'same-tab' : 'new-tab'
}
